import styled from '@emotion/styled';
import { MaxMobileWindowWidth, RacemapColors } from '@racemap/utilities/consts/common';
import { getEventTypeLabel } from '@racemap/utilities/functions/events';
import { isNotEmptyString } from '@racemap/utilities/functions/utils';
import { Space, Typography } from 'antd';
import type { Immutable } from 'immer';
import { type FC, useState } from 'react';
import { Tristate } from 'src/components/utils/tristateTask';
import { useInterval } from 'usehooks-ts';
import type { CurrentEvent } from '../../../store/events/events_reducers';
import { useStore } from '../../../store/reducers';
import { IconFail, IconOk, IconSpinner } from '../../BasicComponents/Icons';
import { RelativeTime } from '../../BasicComponents/TimeDuration/RelativeTime';
import { EventInfoBadges } from '../../EventInfoBadges';

const { Text } = Typography;

interface Props {
  event: Immutable<CurrentEvent>;
}

export const EventMetaInfos: FC<Props> = ({ event }) => {
  const typeLabel = getEventTypeLabel(event.type);
  return (
    <Container>
      <TitleAndBadges>
        <Space>
          <Text type="secondary" strong>
            {typeLabel}
          </Text>
          {isNotEmptyString(event.name) ? (
            <Text title={event.name} strong>
              {event.name}
            </Text>
          ) : (
            <Text italic>Untitled</Text>
          )}
        </Space>
        <EventInfoBadges event={event} />
      </TitleAndBadges>
      <UpdatedAtLabel event={event} />
    </Container>
  );
};

const Container = styled.div`
  padding-left: 10px;
  padding-right: 10px;
  overflow: hidden;

  @media (max-width: ${MaxMobileWindowWidth}px) {
    max-width: calc(100% - 55px);
  }
`;

const TitleAndBadges = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 15px;
  flex-wrap: nowrap;
  white-space: nowrap;
`;

const UpdatedAtLabel: FC<{ event: Immutable<CurrentEvent> }> = ({ event }) => {
  const [count, setCount] = useState(0);
  useInterval(() => {
    setCount(count + 1);
  }, 1000);
  const savingState = useStore((s) => s.events.savingState);

  return (
    <div style={{ color: RacemapColors.DarkGray }}>
      {savingState === Tristate.SPINNING ? (
        <>
          <IconSpinner /> saving
        </>
      ) : savingState === Tristate.FAILURE ? (
        <>
          <IconFail /> Save failed
        </>
      ) : (
        <>
          <IconOk /> saved <RelativeTime time={event.updatedAt} />
        </>
      )}
    </div>
  );
};
